import React, { useEffect } from "react"
import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Badge } from "../../../components/ui/badge"
import { Card, CardContent } from "../../../components/ui/card"
import { Separator } from "../../../components/ui/separator"
import { Copy, Check, Calendar, User, Shield, Clock, Building, RefreshCw, XCircle, User2Icon } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip"
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../../../components/ui/sheet"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    // @ts-ignore
} from "../../../components/ui/select";
import { usePermissionManagement } from "../../Services/PermissionManagement"
import { ScrollArea } from "../../../components/ui/scroll-area"
import { toast } from "react-toastify"

const UserManagementDetailsView = ({ user, open, onOpenChange, resendCallBack, allRoles = [], initialTab = "details" }) => {
    const { assignUserByRole } = usePermissionManagement();
    const [copied, setCopied] = useState<string | null>(null);
    const [selectedRole, setSelectedRole] = useState<string | null>(null);

    useEffect(() => {
        setSelectedRole(user?.roleId || null);
    }, [user])

    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text)
        setCopied(field)
        setTimeout(() => setCopied(null), 2000)
    }

    // Format date
    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    // Get status badge color
    const getStatusBadge = (status: string) => {
        switch (status) {
            case "Active":
                return (
                    <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Active
                    </Badge>
                )
            case "Pending":
                return (
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Pending
                    </Badge>
                )
            default:
                return (
                    <Badge variant="outline" className="bg-gray-100 text-gray-800">
                        {status}
                    </Badge>
                )
        }
    }

    // Get user type badge
    const getUserTypeBadge = (type: string) => {
        switch (type) {
            case "Admin":
                return (
                    <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Admin
                    </Badge>
                )
            case "User":
                return (
                    <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                        <User className="h-3 w-3" />
                        User
                    </Badge>
                )
            default:
                return <Badge className="bg-gray-100 text-gray-800">{type}</Badge>
        }
    }

    // Get user avatar
    const getUserAvatar = (email: string) => {
        const initials = email ? email.substring(0, 2).toUpperCase() : "??"

        return (
            <Avatar className="h-20 w-20 border">
                <AvatarImage src="/placeholder.svg" alt={email || "User"} />
                <AvatarFallback className="bg-purple-100 text-purple-800 text-xl">{initials}</AvatarFallback>
            </Avatar>
        )
    }

    // Extract name from email
    const getNameFromEmail = (email: string) => {
        if (!email) return "Unknown User"
        const parts = email.split("@")[0].split(".")
        return parts
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ")
            .replace(/[0-9]/g, "")
    }

    const handleResend = () => {
        resendCallBack(user?.id);
    }

    const onChange = (e) => {
        const value = e;
        setSelectedRole(value);
    };

    const handleAssignRole = () => {
        assignUserByRole(selectedRole, user?.id)
            .then((response) => {
                console.log("Response from assigning role to user : ", response);
                if (response?.status === 200) {
                    toast.success("Role assigned");
                    return;
                }
                toast.error("Role assigned failed");
            })
            .catch((err) => {
                toast.error("Role assigned failed");
                console.error("Error assigning role:", err);
            });
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full min-w-[600px] p-0 overflow-auto user-detail-view">
                <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b detail-header">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="bg-purple-100 p-2 rounded-lg">
                                <User2Icon className="h-6 w-6 text-purple-600" />
                            </div>
                            <div>
                                <SheetTitle className="text-xl font-bold text-gray-900">User Management</SheetTitle>
                                <p className="text-sm text-gray-600 mt-1">{user.userType} • {user.notificationStatus === "Active" ? "Active User" : "Invited User"}</p>
                                <div className="ml-auto">{getStatusBadge(user.notificationStatus)}</div>
                            </div>
                        </div>
                    </div>
                    <Button variant="outline" onClick={() => onOpenChange(false)} className="close-panel">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
                    </Button>
                </SheetHeader>

                <Tabs defaultValue={initialTab} className="w-full">
                    <div className="px-6 pt-6">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="details">User Details</TabsTrigger>
                            <TabsTrigger value="organization">Organization</TabsTrigger>
                            <TabsTrigger value="permission">Permission</TabsTrigger>
                        </TabsList>
                    </div>

                    <div className="p-6">
                        <ScrollArea className="h-[650px]">
                            <TabsContent value="details" className="mt-0 space-y-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4">
                                        {getUserAvatar(user?.userEmail || user?.email)}
                                        <div>
                                            <h3 className="text-xl font-semibold">{user?.fullName || getNameFromEmail(user?.userEmail || user?.email)}</h3>
                                            <div className="flex items-center mt-1 space-x-2">
                                                {getUserTypeBadge(user.userType)}
                                                {getStatusBadge(user.notificationStatus)}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Separator />

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Email</p>
                                        <TooltipProvider>
                                            <div className="flex items-center">
                                                <p className="font-medium truncate mr-2">{user?.userEmail || user?.email}</p>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => copyToClipboard(user?.userEmail || user?.email, "email")}
                                                        >
                                                            {copied === "email" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Copy email</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TooltipProvider>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Status</p>
                                        <div>{getStatusBadge(user.notificationStatus)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">User Type</p>
                                        <div>{getUserTypeBadge(user.userType)}</div>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">Invited On</p>
                                        <p className="font-medium">{formatDate(user.createdDate)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500 mb-1">User Role</p>
                                        <p className="font-medium">{allRoles && allRoles?.find((item: any) => item.id === user.roleId)?.roleName}</p>
                                    </div>
                                </div>

                                <Card className="bg-gray-50 border">
                                    <CardContent className="p-4 space-y-4">
                                        <div className="flex items-center space-x-3">
                                            <User className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500">Invited By</p>
                                                <p className="font-medium">{user.invitedFromUserName}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <Calendar className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500">Invitation Date</p>
                                                <p className="font-medium">{formatDate(user.createdDate)}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <Clock className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500">Invitation Status</p>
                                                <div>{getStatusBadge(user.notificationStatus)}</div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>


                            </TabsContent>

                            <TabsContent value="organization" className="mt-0 space-y-6">
                                <Card>
                                    <CardContent className="p-4 space-y-4">
                                        <div className="flex items-center space-x-3">
                                            <Building className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500">Organization</p>
                                                <p className="font-medium">{user.invitedFromTenantName}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <Shield className="h-5 w-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm text-gray-500">Organization ID</p>
                                                <TooltipProvider>
                                                    <div className="flex items-center">
                                                        <p className="font-mono text-sm truncate mr-2">{user.invitedFromTenantId}</p>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-6 w-6"
                                                                    onClick={() => copyToClipboard(user.invitedFromTenantId, "orgId")}
                                                                >
                                                                    {copied === "orgId" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Copy Organization ID</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </div>
                                                </TooltipProvider>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardContent className="p-4">
                                        <h3 className="font-semibold mb-3">Technical Details</h3>
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">User ID</span>
                                                <TooltipProvider>
                                                    <div className="flex items-center">
                                                        <span className="font-mono text-sm truncate max-w-[200px]">{user.id}</span>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-6 w-6"
                                                                    onClick={() => copyToClipboard(user.id, "userId")}
                                                                >
                                                                    {copied === "userId" ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p>Copy User ID</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </div>
                                                </TooltipProvider>
                                            </div>
                                            <Separator />
                                            <div className="flex justify-between">
                                                <span className="text-gray-500">Created Date</span>
                                                <span className="font-medium">{formatDate(user.createdDate)}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>

                            <TabsContent value="permission" className="mt-0 space-y-6">
                                <div>
                                    <label className="text-xs font-bold">User Role</label>
                                    <Select
                                        value={selectedRole || undefined}
                                        onValueChange={(event) => onChange(event)}
                                    >
                                        <SelectTrigger
                                            className={"w-full"}
                                        >
                                            <SelectValue placeholder="Select User Type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {allRoles?.map((item, index) => {
                                                return (
                                                    <SelectItem key={index} value={item?.id}>
                                                        {item?.roleName}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>

                                    <div className="border-t mt-4 flex justify-end gap-2">
                                        <Button className="bg-primary hover:bg-[#5f50d8]" onClick={handleAssignRole}>
                                            Assign Role
                                        </Button>
                                    </div>
                                </div>
                            </TabsContent>
                        </ScrollArea>
                        <div className="absolute bottom-0 left-0 right-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3">

                            <Button className="bg-primary hover:bg-[#5f50d8]" onClick={handleResend}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Resend Invitation
                            </Button>
                        </div>
                    </div>
                </Tabs>
            </SheetContent>
        </Sheet>
    )
}

export default UserManagementDetailsView;